import { Action } from 'redux';

import { HiTabConfig } from '../human-insight/human-insight.model';
import { Tab } from '../insight/experimental.tabset';
import { TabContext, TabType } from '../insight/tabset.model';
import { PiTabConfig } from '../performance-insight/performance-insight.model';

import {
    ActionType,
    InitAction,
    InitResultAction,
    TabsetAddTabAction,
    TabsetCloseTabAction,
    TabsetRenameTabAction,
    TabsetReorderTabAction,
    TabsetSetTabStateAction,
    TabsetSwitchTabAction,
} from './insight-explorer.action';
import { defaultState, RootState } from './insight-explorer.state';

interface ReducerFns {
    [key: string]: (state: RootState, action: Action<ActionType>) => RootState;
}

const rootFns: ReducerFns = {
    [ActionType.INSIGHT_EXPLORER__ROOT__INIT](
        state,
        action: InitAction,
    ): RootState {
        return {
            ...state,
            pending: {
                ...state.pending,
                init: true,
            },
        };
    },
    [ActionType.INSIGHT_EXPLORER__ROOT__INIT_RESULT](
        state,
        action: InitResultAction,
    ): RootState {
        return {
            ...(action.state || state),
            pending: {
                ...state.pending,
                init: false,
            },
        };
    },
};

const tabsetFns: ReducerFns = {
    [ActionType.INSIGHT_EXPLORER__TABSET__SWITCH_TAB](
        state,
        action: TabsetSwitchTabAction,
    ): RootState {
        if (state.selectedTab === action.id) {
            return state;
        }
        return {
            ...state,
            selectedTab: action.id,
        };
    },
    [ActionType.INSIGHT_EXPLORER__TABSET__REORDER_TAB](
        state,
        action: TabsetReorderTabAction,
    ): RootState {
        const tabs = [...state.tabs];
        tabs.splice(
            action.index,
            0,
            tabs.splice(
                tabs.findIndex((d) => d.id === action.id),
                1,
            )[0],
        );
        return {
            ...state,
            tabs,
        };
    },
    [ActionType.INSIGHT_EXPLORER__TABSET__ADD_TAB](
        state,
        action: TabsetAddTabAction,
    ): RootState {
        const tabs = [...state.tabs];
        let selectedTab = state.selectedTab;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (action.tab && action.tab.ctx && action.tab.ctx.config) {
            let tab: Tab<TabContext<any>>;
            if (action.tab.ctx.type === TabType.HUMAN) {
                tab = tabs
                    .filter((e) => e.ctx.type === TabType.HUMAN)
                    .find(
                        (e: Tab<TabContext<HiTabConfig>>) =>
                            e.ctx.config.identifier ===
                                action.tab.ctx.config.identifier &&
                            e.ctx.config.userId ===
                                action.tab.ctx.config.userId,
                    );
            }
            if (action.tab.ctx.type === TabType.PERFORMANCE) {
                tab = tabs
                    .filter((e) => e.ctx.type === TabType.PERFORMANCE)
                    .find(
                        (e: Tab<TabContext<PiTabConfig>>) =>
                            e.ctx.config.identifier ===
                            action.tab.ctx.config.identifier,
                    );
            }
            if (!tab) {
                tabs.splice(tabs.length - 1, 0, action.tab);
                selectedTab = action.tab.id;
            } else {
                selectedTab = tab.id;
            }
        }
        return {
            ...state,
            tabs,
            selectedTab,
        };
    },
    [ActionType.INSIGHT_EXPLORER__TABSET__CLOSE_TAB](
        state,
        action: TabsetCloseTabAction,
    ): RootState {
        const tabs = [...state.tabs];
        const index = tabs.findIndex((e) => e.id === action.id);
        if (index !== -1) {
            tabs.splice(index, 1);
        }
        let selectedTab = state.selectedTab;
        if (state.selectedTab === action.id) {
            selectedTab = tabs.every((e) => e.ctx.type === TabType.ADD)
                ? (state.selectedTab = undefined)
                : tabs[Math.max(0, index - 1)].id;
        }
        return {
            ...state,
            tabs,
            selectedTab,
        };
    },
    [ActionType.INSIGHT_EXPLORER__TABSET__RENAME_TAB](
        state: RootState,
        action: TabsetRenameTabAction,
    ): RootState {
        const tab = state.tabs.find((e) => e.id === action.id);
        if (tab && tab.ctx.label !== action.label) {
            if (!tab.ctx.originalLabel) {
                tab.ctx.originalLabel = tab.ctx.label;
            }
            tab.ctx.label = !action.label
                ? tab.ctx.originalLabel
                : action.label;
        }
        return {
            ...state,
            tabs: [...state.tabs],
        };
    },
    [ActionType.INSIGHT_EXPLORER__TABSET__SET_TAB_STATE](
        state,
        action: TabsetSetTabStateAction,
    ): RootState {
        const tab = state.tabs.find((e) => e.id === action.id);
        if (!tab) {
            return state;
        }
        if (action.isDefault != null) {
            // eslint-disable-next-line no-return-assign
            state.tabs.forEach((e) => (e.ctx.isDefault = undefined));
        }
        tab.ctx.isDefault =
            action.isDefault == null ? tab.ctx.isDefault : action.isDefault;
        tab.ctx.isFavourite =
            action.isFavourite == null
                ? tab.ctx.isFavourite
                : action.isFavourite;
        return {
            ...state,
            tabs: [...state.tabs],
        };
    },
};

const reducerFns: ReducerFns = {
    ...rootFns,
    ...tabsetFns,
};

const insightExplorer = (
    state: RootState = defaultState(),
    action: Action<ActionType>,
): RootState => {
    const fn = reducerFns[action.type];
    if (fn) {
        return fn(state, action);
    }
    return state;
};

export default {
    insightExplorer,
};
